import { useDispatch } from "react-redux";
import { useMutation } from "@apollo/client";
import { mdiClockOutline, mdiDeleteOutline, mdiPencil, mdiReplay } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import { ArrowDotRightIcon } from "components/SVGIcons";
import { convertDiffToAge, convertTime, parseError } from "helpers/convertFormData";
import { formatNumber } from "helpers/formatNumber";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { truckInfo, truckLocation } from "helpers/truck";
import { ITruckData } from "models/Truck";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import { MUTATION_REMOVE_TRUCK } from "../../List/query";
import { MUTATION_UPDATE_TRUCK_AGE } from "../../Post/query";
import styles from "./style.module.scss";

interface IProps {
  data: ITruckData;
  goDetail?: (id: string | number) => void;
  editTruckItem?: (id: string | number) => void;
  goMatches?: (id: string | number) => void;
  hideFooter?: boolean;
  refresh?: VoidFunction;
}

export default function QueryPostedTruckItem({
  data,
  goDetail,
  editTruckItem,
  goMatches,
  refresh,
  hideFooter
}: Readonly<IProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const [isDeleteConfirmModalOpen, toggleDeleteConfirmModal] = useToggle();
  const [isRefreshAgeConfirmModalOpen, toggleRefreshAgeConfirmModal] = useToggle();

  const {
    id,
    timePosted,
    originCity,
    originRegion,
    originRadius,
    destinationCity,
    destinationRegion,
    destinationRadius,
    timeStart,
    timeEnd,
    user,
    targetRate,
    linehaulDistance,
    matches,
    trailerTypes,
    length,
    weight
  } = data;
  const truckAge = convertDiffToAge(dayjs().diff(dayjs(timePosted), "milliseconds"));

  const [updateTruckAge] = useMutation(MUTATION_UPDATE_TRUCK_AGE, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const [removeTruck] = useMutation(MUTATION_REMOVE_TRUCK, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const updateMyTruckAge = async () => {
    try {
      dispatch(handleLoading(true));
      const { data } = await updateTruckAge({ variables: { id: id } });
      if (data.updateTruck.truck) {
        showSuccessMessage("Successfully updated post.");
        toggleRefreshAgeConfirmModal();
      } else {
        showErrorMessage("Failed to update post.");
      }
      refresh && refresh();
    } finally {
      dispatch(handleLoading(false));
    }
  };

  const removeMyTruck = async () => {
    try {
      dispatch(handleLoading(true));
      const { data } = await removeTruck({ variables: { id: id } });
      if (data.removeTruck.success) {
        showSuccessMessage("Truck removed successfully.");
      } else {
        showErrorMessage("Failed to remove truck.");
      }
      toggleDeleteConfirmModal();
      refresh && refresh();
    } finally {
      dispatch(handleLoading(false));
    }
  };

  return (
    <li className={clsx(styles.truckItem, !hideFooter && "rounded-b-base")}>
      <div className={styles.cardHeader}>
        <div className={styles.truckDestination}>
          <span>{truckLocation(originCity, originRegion, originRadius, "origin")}</span>
          <ArrowDotRightIcon className="flex-none" />
          <span>
            {truckLocation(destinationCity, destinationRegion, destinationRadius, "destination")}
          </span>
        </div>
        <span className="text-xs">{truckInfo(trailerTypes, length, weight)}</span>
      </div>
      <ul className={styles.cardContent}>
        <li>
          <span className="text-xs">Company Type</span>
          <span className="capitalize">{user.userType}</span>
        </li>
        <li>
          <span className="text-xs">Distance</span>
          <span>{linehaulDistance ? `${linehaulDistance} mi` : "0 mi"}</span>
        </li>
        <li>
          <div className="flex items-center gap-1.5 text-xs ">
            <span>Age</span>
            <Icon path={mdiClockOutline} className="size-3" />
          </div>
          <span>{truckAge}</span>
        </li>
        <li>
          <span className="text-xs">Ready Start</span>
          <span>{convertTime(timeStart).day}</span>
          <span className="text-xs">{convertTime(timeStart).time}</span>
        </li>
        <li>
          <span className="text-xs">Ready End</span>
          <span>{convertTime(timeEnd).day}</span>
          <span className="text-xs">{convertTime(timeEnd).time}</span>
        </li>
        <li>
          <span className="text-xs">Rate Per Mile</span>
          <span>{`${formatNumber(targetRate, 2)}/ mile`}</span>
        </li>
      </ul>

      {!hideFooter && (
        <div className={styles.cardFooter}>
          <div className={styles.cardFooterLeft}>
            <Button
              rounded
              iconButton
              className="!size-10"
              onClick={() => {
                editTruckItem && editTruckItem(id);
              }}>
              <Icon path={mdiPencil} className="m-auto" />
            </Button>
            <Button rounded iconButton className="!size-10" onClick={toggleDeleteConfirmModal}>
              <Icon path={mdiDeleteOutline} className="m-auto" />
            </Button>
            <Button rounded iconButton className="!size-10" onClick={toggleRefreshAgeConfirmModal}>
              <Icon path={mdiReplay} className="m-auto" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            {matches && goMatches ? (
              <Button
                onClick={() => goMatches && goMatches(data.id)}
                variant="outline"
                rounded
                className="!border-success !text-success text-sm !px-3.5">
                {matches === 1 ? "Match" : "Matches"} {matches}
              </Button>
            ) : (
              <span className="mobile:text-sm desktop:mx-10">No Matches Yet</span>
            )}

            <Button
              onClick={() => goDetail && goDetail(data.id)}
              variant="outline"
              rounded
              className="text-sm !px-3.5">
              Details
            </Button>
          </div>
        </div>
      )}

      <ConfirmationModal
        isConfirmModalOpen={isDeleteConfirmModalOpen}
        toggleConfirmModal={toggleDeleteConfirmModal}
        title="Confirm Truck Removal"
        content={
          <div>
            <p>{`Are you sure you would like to remove your truck form ${originCity}, ${originRegion.map((item) => item.originStates).join(", ")} to ${destinationCity}, ${destinationRegion.map((item) => item.destinationStates).join(", ")}?`}</p>
            <p>This truck post will be moved to history and removed from this view.</p>
            <p>{`You posted this on ${dayjs(timePosted).format("dddd, MMMM D, YYYY HH:mm A")}`}</p>
          </div>
        }
        labelOKButton="Remove"
        onOK={removeMyTruck}
        isHideHeaderIcon
      />

      <ConfirmationModal
        isConfirmModalOpen={isRefreshAgeConfirmModalOpen}
        toggleConfirmModal={toggleRefreshAgeConfirmModal}
        title="Refresh Age of Post"
        content="Are you sure want to refresh the age of this post to show up newer to other users?"
        labelOKButton="Refresh"
        onOK={updateMyTruckAge}
      />
    </li>
  );
}
